import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes.constants';
import notificationService from '../../services/notificationService';

const NotificationBell = ({ userRole }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // En cas d'erreur, on met quand même l'icône visible
      setUnreadCount(0);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await notificationService.getNotifications({
        limit: 10,
        page: 1,
      });
      if (response.success) {
        setNotifications(response.data || []);
        setUnreadCount(response.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only show for admin and vendor - return null after all hooks
  if (userRole !== 'admin' && userRole !== 'vendor') {
    return null;
  }

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation();
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      const deletedNotification = notifications.find(n => n._id === notificationId);
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Détecter les notifications de demande de suppression
    const isDeletionNotification = 
      notification.type === 'VEHICLE_DELETION' || 
      notification.type === 'deletion_request' ||
      (notification.title && (notification.title.toLowerCase().includes('suppression') || notification.title.toLowerCase().includes('deletion'))) ||
      (notification.message && (notification.message.toLowerCase().includes('suppression') || notification.message.toLowerCase().includes('deletion')));

    // Si c'est une notification de suppression avec un véhicule lié, ouvrir le modal
    if (isDeletionNotification && notification.relatedEntity?.type === 'Vehicle' && notification.relatedEntity?.id && userRole === 'admin') {
      navigate(ROUTES.ADMIN_VEHICLES_LIST, {
        state: {
          openDeletionModal: true,
          vehicleId: notification.relatedEntity.id,
        }
      });
    } else if (notification.relatedEntity?.type === 'Vehicle' && notification.relatedEntity?.id) {
      if (userRole === 'admin') {
        const isVehicleUpdateNotification =
          notification.type === 'VEHICLE_UPDATED' ||
          notification.type === 'VEHICLE_REJECTED_UPDATED' ||
          notification.type === 'VEHICLE_PENDING' ||
          notification.type === 'VEHICLE_RESUBMISSION' ||
          notification.type === 'VEHICLE_ADMIN_UPDATE' ||
          /modifi|mise à jour|mis à jour/i.test(`${notification.title || ''} ${notification.message || ''}`);

        if (isVehicleUpdateNotification) {
          navigate(ROUTES.ADMIN_VEHICLES_REVIEW_BY_ID(notification.relatedEntity.id));
        } else {
          navigate(`${ROUTES.ADMIN_VEHICLES_LIST}?highlight=${notification.relatedEntity.id}`);
        }
      } else if (userRole === 'vendor') {
        // Pour les vendors, rediriger vers leur liste de véhicules
        navigate(ROUTES.VENDOR_VEHICLES_LIST || '/vendor/vehicles');
      }
    } else if (notification.relatedEntity?.type === 'Agency' && notification.relatedEntity?.id) {
      navigate(ROUTES.ADMIN_PENDING_AGENCIES);
    } else if (notification.type === 'KYC_PENDING' && notification.relatedEntity?.type === 'User' && notification.relatedEntity?.id) {
      navigate(`${ROUTES.ADMIN_USERS}?tab=kyc-pending`);
    }
    if (!notification.read) {
      handleMarkAsRead(notification._id, { stopPropagation: () => {} });
    }
    setIsOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays < 7) return `Il y a ${diffDays} j`;
    return date.toLocaleDateString('fr-FR');
  };

  // Fonction pour formater le message de notification (résumé pour VEHICLE_REJECTED et VEHICLE_REJECTED_UPDATED)
  const formatNotificationMessage = (notification) => {
    if (notification.type === 'VEHICLE_REJECTED') {
      // Extraire les informations du message complet
      const message = notification.message || '';
      
      // Chercher le pattern: "votre véhicule [marque] [modèle] ([année] sous le matricule [matricule])"
      // Exemple: "votre véhicule toyota yaris (2025 sous le matricule 10000-0-00)"
      const vehicleMatch = message.match(/votre véhicule\s+([^(]+)\s+\(([^)]+)\s+sous le matricule\s+([^)]+)\)/i);
      
      if (vehicleMatch) {
        const brandModel = vehicleMatch[1].trim();
        const year = vehicleMatch[2].trim();
        const matricule = vehicleMatch[3].trim();
        
        return `Merci d'avoir soumis votre véhicule ${brandModel} (${year}) sur la plateforme Carvy sous Le numéro de plaque d'immatriculation ${matricule}...`;
      }
      
      // Fallback: chercher un pattern sans matricule
      const simpleMatch = message.match(/votre véhicule\s+([^(]+)\s+\(([^)]+)\)/i);
      if (simpleMatch) {
        const brandModel = simpleMatch[1].trim();
        const year = simpleMatch[2].trim();
        return `Merci d'avoir soumis votre véhicule ${brandModel} (${year}) sur la plateforme Carvy...`;
      }
      
      // Si aucun pattern ne correspond, retourner les premiers caractères avec "..."
      return message.length > 100 ? message.substring(0, 100) + '...' : message;
    }
    
    if (notification.type === 'VEHICLE_REJECTED_UPDATED') {
      const message = notification.message || '';
      const rejectionMatch = message.match(/Motif du refus précédent\s*:\s*(.+)/is);
      const rejectionReason = rejectionMatch?.[1]?.trim();
      if (
        rejectionReason &&
        rejectionReason !== 'undefined' &&
        rejectionReason !== 'null'
      ) {
        return rejectionReason.length > 150 ? rejectionReason.substring(0, 150) + '...' : rejectionReason;
      }
      if (message && message !== 'undefined') {
        return message.length > 150 ? message.substring(0, 150) + '...' : message;
      }
      return notification.title || 'Véhicule modifié après refus';
    }
    
    // Pour les autres types de notifications, retourner le message complet
    return notification.message;
  };

  // Toujours afficher l'icône, même en cas d'erreur
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-[#27489E] transition-colors"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-[#27489E] hover:text-[#1e3a7a] flex items-center gap-1"
                  title="Tout marquer comme lu"
                >
                  <CheckCheck className="w-4 h-4" />
                  Tout lire
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notifications list */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">Chargement...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">Aucune notification</div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition ${
                    !notification.read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">{formatNotificationMessage(notification)}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(notification.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {!notification.read && (
                        <button
                          onClick={(e) => handleMarkAsRead(notification._id, e)}
                          className="p-1 text-gray-400 hover:text-[#27489E]"
                          title="Marquer comme lu"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(notification._id, e)}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  // Navigate to the most relevant page based on notification types and user role
                  const hasVehicleNotifications = notifications.some(n =>
                    n.type === 'VEHICLE_PENDING' ||
                    n.type === 'VEHICLE_REJECTED' ||
                    n.type === 'VEHICLE_REJECTED_UPDATED' ||
                    n.type === 'VEHICLE_UPDATED' ||
                    n.type === 'VEHICLE_RESUBMISSION'
                  );
                  const hasAgencyNotifications = notifications.some(n => n.type === 'AGENCY_PENDING');
                  const hasKYCNotifications = notifications.some(n => n.type === 'KYC_PENDING');
                  
                  if (userRole === 'vendor') {
                    // Pour les vendors, rediriger vers leur liste de véhicules
                    navigate(ROUTES.VENDOR_VEHICLES_LIST || '/vendor/vehicles');
                  } else if (userRole === 'admin') {
                    // Pour les admins, navigation selon le type de notification
                    if (hasVehicleNotifications && hasAgencyNotifications) {
                      navigate(ROUTES.ADMIN_DASHBOARD);
                    } else if (hasVehicleNotifications) {
                      navigate(ROUTES.ADMIN_VEHICLES_LIST);
                    } else if (hasAgencyNotifications) {
                      navigate(ROUTES.ADMIN_PENDING_AGENCIES);
                    } else if (hasKYCNotifications) {
                      navigate(`${ROUTES.ADMIN_USERS}?tab=kyc-pending`);
                    } else {
                      navigate(ROUTES.ADMIN_DASHBOARD);
                    }
                  }
                  setIsOpen(false);
                }}
                className="text-xs text-[#27489E] hover:text-[#1e3a7a]"
              >
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;

